import React, { useState, useEffect } from "react";
import {
  CheckCircle,  Users,  BookOpen,
  ArrowRight, Clock,   GraduationCap,
  X, User, Phone, Mail, MapPin, GraduationCap as CourseIcon
} from "lucide-react";
import { submitCourseRegistration } from "../services/contactService";

const Courses = () => {
  // --- State Management ---
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    email: '',
    city: '',
    course: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  // --- Navigation Handler ---
  const HEADER_OFFSET = 100;

  const handleCourseNavigation = (courseId, sectionId) => {
    const target = document.getElementById(courseId);
    if (target) {
      // Scroll to the element, then offset for any fixed header
      const elementTop = target.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementTop - HEADER_OFFSET,
        behavior: "smooth"
      });
    } else if(sectionId) {
      // Fallback: scroll to section if specific card is not found
      const section = document.getElementById(sectionId);
      if (section) {
        const sectionTop = section.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: sectionTop - HEADER_OFFSET,
          behavior: "smooth"
        });
      }
    }
  };

  // --- Form Handlers ---
  const openRegistrationPopup = (courseTitle = '') => {
    setSelectedCourse(courseTitle);
    setFormData(prev => ({ ...prev, course: courseTitle }));
    setIsPopupOpen(true);
    setSubmitMessage('');
    document.body.style.overflow = 'hidden';
  };

  const closeRegistrationPopup = () => {
    setIsPopupOpen(false);
    setFormData({
      name: '',
      contact: '',
      email: '',
      city: '',
      course: selectedCourse
    });
    setSubmitMessage('');
    document.body.style.overflow = 'unset';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };



  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Basic validation
    if (!formData.name || !formData.contact || !formData.email || !formData.course) {
      setSubmitMessage('Please fill in all required fields.');
      setIsSubmitting(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitMessage('Please enter a valid email address.');
      setIsSubmitting(false);
      return;
    }

    // Phone validation (basic)
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(formData.contact.replace(/\D/g, ''))) {
      setSubmitMessage('Please enter a valid 10-digit contact number.');
      setIsSubmitting(false);
      return;
    }

    // Submit to WordPress REST API (sends admin email + stores in WP + auto-replies to user)
    const result = await submitCourseRegistration(formData);

    // Also fire Google Sheets silently in background (no-cors, non-blocking)
    const SHEETS_URL = 'https://script.google.com/macros/s/AKfycbwk4b4St7gVKZVJotKhymGZwZxidYMt_jktbYyiDIN6PCLDzqj4ehQBYbV5hyAunSJbvA/exec';
    fetch(SHEETS_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...formData, timestamp: new Date().toISOString() })
    }).catch(() => {}); // Silent — never blocks the UX

    if (result.success) {
      setSubmitMessage('Registration successful! We will contact you soon.');
    } else {
      // Show error but still close after delay so user isn't stuck
      setSubmitMessage(result.message || 'Registration submitted! We will contact you soon.');
    }

    setTimeout(() => {
      closeRegistrationPopup();
    }, 2000);

    setIsSubmitting(false);
  };

  // ---- DATA ----
  const courses = [
    {
      title: "UPSC",
      subtitle: "Union Public Service Commission",
      gradient: "bg-gradient-to-br from-purple-500 via-purple-400 to-blue-400",
      features: [
        { name: "UPSC Foundation Course", id: "upsc-foundation", section: "upsc" },
        { name: "Integrated Batch (All Stages)", id: "upsc-integrated", section: "upsc" },
        { name: "Personalized Mentorship Programme", id: "personalized-mentorship", section: "upsc" },
        { name: "5-Month Answer Writing Programme", id: "answer-writing", section: "upsc" },
        { name: "Optional Subjects", id: "optional-subjects", section: "upsc" },
        { name: "Essay Writing Programme", id: "essay-writing", section: "upsc" },
        { name: "Ethics Course", id: "ethics-plus", section: "upsc" }
      ],
    },
    {
      title: "MPSC",
      subtitle: "Maharashtra Public Service Commission",
      gradient: "bg-gradient-to-br from-green-400 via-teal-400 to-cyan-400",
      features: [
        { name: "MPSC फाउंडेशन बॅच", id: "mpsc-foundation", section: "mpsc" },
        { name: "MPSC इंटिग्रेटेड बॅच", id: "mpsc-integrated", section: "mpsc" },
        { name: "MPSC वैयक्तिक मेंटरशिप प्रोग्रॅम", id: "mpsc-personalized-mentorship", section: "mpsc" },
        { name: "MPSC उत्तरलेखन प्रोग्रॅम (५ महिने)", id: "mpsc-answer-writing", section: "mpsc" },
        { name: "निबंध लेखन प्रोग्रॅम – MPSC", id: "mpsc-essay", section: "mpsc" },
        { name: "एथिक्स कोर्स – MPSC स्पेश", id: "ethics-mentorship", section: "mpsc" }
      ],
    },
    {
      title: "CAPF",
      subtitle: "Central Armed Police Forces",
      gradient: "bg-gradient-to-br from-orange-400 via-red-400 to-pink-500",
      features: [
        { name: "CAPF Mentorship Programme", id: "capf-mentorship", section: "capf" },
        { name: ".", id: "capf-mentorship", section: "capf" },
        { name: ".", id: "capf-mentorship", section: "capf" },
        { name: ".", id: "capf-mentorship", section: "capf" },
        { name: ".", id: "capf-mentorship", section: "capf" },

      ],
    },
  ];

  const upscCourses = [
    {
      id: 'upsc-foundation',
      title: "UPSC Foundation Course",
      subtitle: "A Structured Beginning for Serious Aspirants",
      description: "Specially curated long-term preparatory program for college students and early-stage aspirants. Build a strong foundation with NCERT-based learning, current affairs integration, and answer writing practice. Access valid till Mains 2026.",
      highlights: [
        "Comprehensive Introduction to UPSC (Prelims, Mains & Interview)",
        "NCERT-Focused Conceptual Learning",
        "Newspaper and Current Affairs Orientation",
        "Foundations of Answer Writing and Note Making",
        "Map Reading and Geographical Understanding",
        "Monthly Current Affairs Magazine & Red Books"
      ],
      popular: true,
      rating: 4.9,
      students: "2,500+",
      duration: "Long-term (Valid till Mains 2026)",
      gradient: "from-blue-600 via-indigo-600 to-purple-600",
      targetAudience: "College Students (1st to final year)"
    },
    {
      id: 'upsc-integrated',
      title: "Integrated Batch",
      subtitle: "Prelims | Mains | Interview",
      description: "Complete UPSC preparation program covering all three stages in a single, well-structured course. Includes GS (Prelims & Mains), Essay, Ethics, Current Affairs, Optional Subjects, and Interview Guidance with personalized mentorship.",
      highlights: [
        "Complete 360° Preparation (All 3 stages)",
        "Expert Faculty & Subject Specialists",
        "Test Series for Every Component",
        "Current Affairs Integration (Daily & Monthly)",
        "Personalized Mentorship Sessions",
        "Updated Study Material & Monthly Magazines"
      ],
      recommended: true,
      duration: "Access till UPSC Mains 2026",
      gradient: "from-emerald-600 via-teal-600 to-cyan-600"
    },
    {
      id: 'personalized-mentorship',
      title: "Personalized Mentorship Programme",
      subtitle: "Strategic Guidance for Success",
      description: "At DimensionIAS, we believe smart strategy and consistent support matter more than just hard work. Our Mentorship Programme offers personalized, one-on-one guidance to help you stay on track, motivated, and exam-ready with clarity and confidence.",
      highlights: [
        "One-on-one sessions with UPSC Toppers & Experts",
        "Customized study plans tailored to your strengths & weaknesses",
        "Regular progress tracking with actionable feedback",
        "Clarity on 'what to study' and 'how to study'",
        "Strategic planning based on your timeline and background",
        "Motivation, accountability & consistent support throughout"
      ],
      gradient: "from-indigo-600 via-purple-600 to-pink-600"
    },
    {
      id: 'answer-writing',
      title: "5-Month Answer Writing Programme",
      subtitle: "Mastering Mains Answer Writing",
      description: "Intensive writing-focused course to develop skills, speed, and structure for high-quality Mains answers. Daily practice with expert evaluation and personalized feedback for GS, Essay & Ethics papers.",
      highlights: [
        "Daily and Weekly Mains Writing Practice",
        "Expert Evaluation & Improvement Suggestions",
        "UPSC-level Question Framing",
        "Progress Tracking & Performance Analysis",
        "Mentorship Support for Answer Structuring",
        "Section-wise and Paper-wise Model Questions"
      ],
      gradient: "from-purple-600 via-pink-600 to-red-500"
    },
    {
      id: 'optional-subjects',
      title: "Optional Subjects",
      subtitle: "Game-changer for UPSC Mains",
      description: "Choosing the right Optional Subject is a strategic step that greatly impacts your UPSC Mains score and final rank. At DimensionIAS, we guide aspirants through this critical decision with expert mentorship. We offer coaching in Anthropology, Sociology, PSIR, and Public Administration with full syllabus coverage, answer writing practice, and mentorship.",
      highlights: [
        "Optional carries 500 marks—can define your final rank",
        "Choose a subject that genuinely interests you",
        "Consider overlap with GS & Essay for better efficiency",
        "Check availability of study material and mentorship",
        "Analyze syllabus length, past trends, and scoring pattern",
        "Get expert guidance & structured coaching at DimensionIAS"
      ],
      gradient: "from-indigo-600 via-purple-600 to-pink-600"
    },
    {
      id: 'essay-writing',
      title: "Essay Writing Programme",
      subtitle: "Game-changer for UPSC Mains",
      description: "Theme-based course to master UPSC-standard essays with 250 marks at stake. Regular practice, detailed evaluation, strategy sessions with toppers, and model essays for philosophical, social, and economic topics.",
      highlights: [
        "Theme-based Essay Practice",
        "Mentorship from UPSC Toppers (140+ scorers)",
        "Personalized Feedback on Every Essay",
        "Library of Themes, Quotes & Examples",
        "Focus on Clarity, Balance & Originality",
        "Live Strategy Sessions"
      ],
      gradient: "from-orange-600 via-red-600 to-pink-600"
    },
    {
      id: 'ethics-plus',
      title: "Ethics Course",
      subtitle: "GS Paper IV Mastery",
      description: "Enhanced preparation for General Studies Paper IV (Ethics) with conceptual clarity, ethical reasoning development, and structured answer writing skills through integrated approach of theory and case studies.",
      highlights: [
        "Core Concepts & Applied Ethics Coverage",
        "Integrated Theory and Case Studies",
        "Structured Test Series with Feedback",
        "Personalized Mentorship & Progress Tracking",
        "Contemporary Ethical Issues Analysis",
        "Case Study Based Learning"
      ],
      gradient: "from-indigo-600 via-purple-600 to-pink-600"
    }
  ];

  const mpscCourses = [
    {
      id: 'mpsc-foundation',
      title: "MPSC Foundation Course",
      subtitle: "Maharashtra-Specific Comprehensive Preparation",
      description: "DimensionIAS चा 'MPSC Foundation Course' हा कॉलेज विद्यार्थी व सुरुवातीच्या तयारीसाठी खास डिझाईन केलेला दीर्घकालीन अभ्यासक्रम आहे. हा कोर्स Prelims, Mains आणि Interview ची ओळख, महाराष्ट्र-केंद्रित मुद्दे, नवीन अभ्यासक्रम, NCERTs, चालू घडामोडी, उत्तरलेखन कौशल्य व योग्य दिशा यांचा समावेश करतो.",
      highlights: [
        "संपूर्ण MPSC परीक्षेची रचनात्मक ओळख",
        "प्रिलिम्स, मेन्स व मुलाखत याविषयी माहिती",
        "अभ्यासक्रम व ट्रेंडचे स्पष्टीकरण, PYQ चा अभ्यास",
        "NCERT वर आधारित सांगोपांग अध्ययन",
        "वृत्तपत्र, चालू घडामोडी, आणि महाराष्ट्र केंद्रित विश्लेषण",
        "उत्तरलेखन आणि नोट्स बनवण्याच्या पद्धतींचा सराव",
        "नकाशा वाचन व महाराष्ट्रातील भौगोलिक मुद्द्यांचे विशेष सत्र",
        "2026 पर्यंत (Mains) कोर्सची अॅक्सेस",
        "थेट अध्याय व रेकॉर्डेड सत्र; २४x७ शैक्षणिक मदत"
      ],
      popular: true,
      gradient: "from-green-600 via-emerald-600 to-teal-600",
      specialFeature: "Bilingual (Marathi & English)",
      duration: "Long-term (Valid till Mains 2026)"
    },
    {
      id: 'mpsc-integrated',
      title: "Integrated Batch – Prelims | Mains | Interview",
      subtitle: "इंटीग्रेटेड बॅच – सर्वसमावेशक अभ्यासक्रम",
      description: "DimensionIAS ची 'Integrated Batch' MPSC च्या तीनही टप्प्यांची (Prelims, Mains, Interview) पूर्ण तयारी पुरवतो. यात General Studies (GS), निबंधलेखन, नीतिशास्त्र (Ethics), विषय पर्यायी (Optional subjects), चालू घडामोडी, आणि व्यक्तिमत्व विकास (Interview Guidance) समाविष्ट आहे.",
      highlights: [
        "सर्व टप्प्यांची, टप्प्याटप्प्याने व काळजीपूर्वक तयारी",
        "अनुभवी प्राध्यापक व विषयतज्ज्ञांचे मार्गदर्शन",
        "प्रत्येक घटकासाठी टेस्ट सिरीज व फीडबॅक",
        "चालू घडामोडींचे नियमित समाकलन (Daily / Monthly)",
        "वैयक्तिक मार्गदर्शन (Personalized Mentorship)",
        "अद्ययावत स्टडी मटेरियल व नोट्स"
      ],
      recommended: true,
      gradient: "from-amber-600 via-orange-600 to-red-600",
      specialFeature: "Maharashtra-Specific Content",
      duration: "Access till MPSC Mains 2026"
    },
    {
      id: 'mpsc-personalized-mentorship',
      title: "वैयक्तिक मार्गदर्शन कार्यक्रम",
      subtitle: "Personalized Mentorship Programme",
      description: "DimensionIAS चा MPSC Foundation Course उमेदवारांची तयारी टप्प्याटप्प्याने व शिस्तबद्ध पद्धतीने घडवतो. अनुभवी प्राध्यापक व विषयतज्ज्ञांचे मार्गदर्शन विद्यार्थ्यांना योग्य दिशा देते।",
      highlights: [
        "सर्व टप्प्यांची काळजीपूर्वक तयारी",
        "अनुभवी प्राध्यापकांचे मार्गदर्शन",
        "प्रत्येक घटकासाठी टेस्ट सिरीज व फीडबॅक",
        "चालू घडामोडींचे नियमित समाकलन",
        "वैयक्तिक मार्गदर्शन (Personalized Mentorship)",
        "अद्ययावत स्टडी मटेरियल व नोट्स"
      ],
      gradient: "from-blue-600 via-cyan-600 to-teal-600",
      specialFeature: "New Descriptive Pattern Focus",
      duration: "Comprehensive Program"
    },
    {
      id: 'mpsc-answer-writing',
      title: "5-Month Answer Writing (MPSC)",
      subtitle: "Descriptive Pattern Mastery",
      description: "MPSC मुख्य परीक्षेत वर्णनात्मक स्वरूपामुळे उत्तरलेखन कौशल्य अनिवार्य झाले आहे. DimensionIAS चा ५ महिने उत्तरलेखन प्रोग्राम ही गरज लक्षात घेऊन तयार केला आहे.",
      highlights: [
        "दररोज आणि साप्ताहिक मुख्य परीक्षेसाठी उत्तरलेखन सराव",
        "विभागानुसार व पेपरनुसार महाराष्ट्र-केंद्रित मॉडेल प्रश्नांचा सराव",
        "प्रत्येक उत्तरावर तज्ज्ञांकडून वैयक्तिक फीडबॅक व सुधारणा सूचना",
        "मुद्देसूद सादरीकरण आणि सुसंगत निष्कर्षासाठी अनुभवी मार्गदर्शन",
        "GS, निबंध, नीतिशास्त्र यासाठी सुसंगत आणि नियमित उत्तरलेखन सराव",
        "उत्तरमांडणी, वेळ व्यवस्थापन आणि विचारक्षमता यात निश्चित प्रगती"
      ],
      gradient: "from-red-600 via-orange-600 to-yellow-600",
      specialFeature: "Serving Officers Guidance",
      duration: "5 Months Intensive"
    },
    {
      id: 'mpsc-essay',
      title: "निबंध लेखन प्रशिक्षण कार्यक्रम",
      subtitle: "Essay Writing Programme - 250 Marks Game-Changer",
      description: "MPSC मुख्य परीक्षेत निबंध हा 'गेम-चेंजर' ठरू शकतो. २५० गुणांचा निबंध पेपर सर्वोच्च रँक मिळवण्यासाठी अतिशय महत्त्वाचा आहे.",
      highlights: [
        "थीम-आधारित व दृष्टिकोनाधारीत अभ्यासक्रम",
        "मागील प्रश्न व चालू ट्रेंडनुसार नियमित निबंध लेखन सराव",
        "प्रत्येक निबंधावर तज्ज्ञांकडून रचनाबद्ध फीडबॅक व सखोल मूल्यांकन",
        "टॉपर्स व मार्गदर्शकांसोबत थेट रणनीती सत्र व निबंध नियोजन",
        "आयडिया जनरेशनसाठी थीम्स, कोट, उदाहरणे, किस्से व डेटा संग्रह",
        "मौलिकता, स्पष्टता व संतुलन असलेले निबंध लिहिण्यासाठी संपूर्ण मार्गदर्शन"
      ],
      gradient: "from-purple-600 via-violet-600 to-indigo-600",
      specialFeature: "Maharashtra Cultural Integration",
      duration: "Theme-based Program"
    },
    {
      id: 'ethics-mentorship',
      title: "ETHICS कोर्स - MPSC साठी विशेष",
      subtitle: "Ethics Course for MPSC",
      description: "Ethics+ प्रोग्राम हा MPSC GS Paper-IV साठी संकल्पनांची स्पष्टता, नैतिक विचार व उत्कृष्ट उत्तरलेखन विकसित करण्यावर केंद्रित आहे.",
      highlights: [
        "Ethics+ मध्ये महाराष्ट्रातील विचारवंतांवर आधारित नैतिक दृष्टिकोन",
        "संकल्पनांपासून केस स्टडीपर्यंत समग्र आणि मूलगामी प्रशिक्षण",
        "Optional विषयासाठी तज्ज्ञ शिक्षक व वैयक्तिक मार्गदर्शन",
        "संपूर्ण कव्हरेज: क्लासरूम, नोट्स, टेस्ट सिरीज व उत्तरलेखन सराव",
        "GS व निबंधाशी ओव्हरलॅप असलेले विषय – जसे PSIR, Sociology",
        "मराठी व इंग्रजी (Bilingual) मध्ये सर्व कोर्सेस उपलब्ध"
      ],
      gradient: "from-red-600 via-orange-600 to-yellow-600",
      specialFeature: "Serving Officers Guidance",
      duration: "Comprehensive Ethics Program"
    },
    {
      id: 'mpsc-optional-subjects',
      title: "MPSC मुख्य परीक्षेसाठी पर्यायी विषयाचे महत्त्व व DimensionIASची वैशिष्ट्ये",
      subtitle: "Personalized Mentorship Programme",
      description: "MPSC मुख्य परीक्षेतील ५०० गुणांपैकी Optional Subject अत्यंत महत्त्वाचा आहे.DimensionIAS विद्यार्थ्यांना वैयक्तिक मार्गदर्शन व योग्य विषय निवडण्यास मदत करते.Anthropology, PSIR, Sociology आणि Public Administration हे प्रमुख पर्याय उपलब्ध आहेत.प्रत्येक विषयासाठी अनुभवी शिक्षक, test series आणि लेखन मार्गदर्शन दिले जाते.Optional निवडताना रुची, अभ्याससामग्री, GS ओव्हरलॅप, वेळ आणि स्कोअर ट्रेंड महत्त्वाचे आहेत.",
      highlights: [
       "Optional subject plays a crucial role in MPSC Mains (500 marks)",
"DimensionIAS offers Anthropology, PSIR, Sociology, and Public Administration",
"Guidance at every stage with expert faculty and personalized mentorship",
"Focus on answer writing, model answers, and peer reviews",
"Bilingual courses available in Marathi and English",
"Complete coverage with classroom programs, updated notes, and test series"
      ],
      gradient: "from-blue-600 via-cyan-600 to-teal-600",
      specialFeature: "New Descriptive Pattern Focus",
      duration: "Comprehensive Program"
    },
  ];

  const capfCourses = [
    {
      id: 'capf-mentorship',
      title: "CAPF Mentorship Programme",
      subtitle: "Your Pathway to Success",
      description: "Comprehensive support system for Central Armed Police Forces examination with personalized guidance from serving officers and exam experts. Strategic preparation for both written exam and interview stages.",
      highlights: [
        "Personalized Study Roadmap",
        "One-on-One Guidance from Serving Officers",
        "Regular Progress Tracking & Feedback",
        "Paper 1 & Paper 2 Strategic Coverage",
        "Mock Tests with Detailed Analysis",
        "Interview Preparation & Personality Development"
      ],
      gradient: "from-red-600 via-orange-600 to-yellow-600",
      specialFeature: "Serving Officers Guidance",
      duration: "Complete CAPF Preparation"
    }
  ];

  const optionalSubjects = [
    { name: "Anthropology", description: "Complete coverage with expert faculty" },
    { name: "Public Administration", description: "High overlap with GS papers" },
    { name: "Sociology", description: "Popular choice with good scoring potential" },
    { name: "Political Science & International Relations (PSIR)", description: "Comprehensive current affairs integration" }
  ];

  // Handle URL hash navigation on mount
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const target = document.querySelector(hash);
      if (target) {
        setTimeout(() => {
          target.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, []);

  // ------------ COMPONENTS -----------

  const CourseOverviewCard = ({ title, subtitle, gradient, features }) => (
    <div className={`relative ${gradient} rounded-2xl p-6 text-white cursor-pointer transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl group`}>
      <div className="absolute inset-0 bg-black/10 rounded-2xl group-hover:bg-black/20 transition-all duration-300"></div>
      <div className="relative z-10">
        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-white/30 transition-all duration-300">
          <GraduationCap className="w-7 h-7 text-white" />
        </div>
        <h3 className="text-2xl font-bold mb-2">{title}</h3>
        <p className="text-white/90 text-sm mb-4 font-medium">{subtitle}</p>
        <div className="space-y-2">
       {features.map((feature, index) => (
  <div
    key={index}
    className="text-sm cursor-pointer text-white/80 hover:text-white transition-colors duration-200"
    onClick={e => {
      e.stopPropagation();
      handleCourseNavigation(feature.id, feature.section);
    }}
  >
    {feature.name}
  </div>
))}

        </div>
        <div className="mt-4 pt-4 border-t border-white/20">
          <p className="text-xs text-white/70 group-hover:text-white/90 transition-colors duration-300">
            Click courses to explore details →
          </p>
        </div>
      </div>
      <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent rounded-2xl"></div>
      </div>
    </div>
  );

  const CourseCard = ({ course, index }) => (
    <div id={course.id} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-6 lg:gap-8 items-center mb-10 sm:mb-12 scroll-mt-24`}>
      <div className="flex-1 space-y-3">
        <div className="space-y-1">
          <h2 className="text-xl lg:text-2xl font-bold text-gray-900">{course.title}</h2>
          <p className="text-sm text-gray-600 font-medium">{course.subtitle}</p>
        </div>
        <p className="text-sm text-gray-700 leading-relaxed">{course.description}</p>
        <div className="flex flex-wrap gap-4 py-2 text-xs">
          {course.duration && (
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-blue-500" />
              <span className="text-gray-700 font-medium">{course.duration}</span>
            </div>
          )}
          {course.targetAudience && (
            <div className="flex items-center gap-1.5">
              <Users className="w-4 h-4 text-green-500" />
              <span className="text-gray-700 font-medium">{course.targetAudience}</span>
            </div>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-2 text-xs">
          {course.highlights.map((highlight, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">{highlight}</span>
            </div>
          ))}
        </div>
        <div className="pt-3">
          <button
            onClick={() => openRegistrationPopup(course.title)}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-2.5 px-6 rounded-full shadow hover:shadow-md transform hover:scale-105 transition-all duration-200 text-xs"
          >
            <span>Register Now</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="flex-1 relative w-full">
        <div className={`relative bg-gradient-to-br ${course.gradient} rounded-2xl p-5 lg:p-6 text-white shadow-xl transform hover:scale-105 transition-all duration-300`}>
          <div className="absolute inset-0 bg-black/10 rounded-2xl"></div>
          <div className="relative space-y-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-bold">Program Highlights</h3>
              <div className="space-y-1.5">
                {course.highlights.slice(0, 4).map((highlight, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-white rounded-full flex-shrink-0"></div>
                    <span className="text-white/90 text-xs">{highlight}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // --------------- MAIN ---------------
  return (
    <div className="pt-20 sm:pt-24 min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Course Overview Section */}
      <section className="py-8 sm:py-10" style={{ backgroundColor: '#E6F0FA' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-4xl md:text-5xl font-bold" style={{ color: '#2D3748' }}>
              Our Courses
            </h2>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: '#2D3748' }}>
              Comprehensive preparation programs designed for your success in competitive examinations
            </p>
          </div>
          {/* Course Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
            {courses.map((course) => (
              <CourseOverviewCard
                key={course.title}
                title={course.title}
                subtitle={course.subtitle}
                gradient={course.gradient}
                features={course.features}
              />
            ))}
          </div>
          {/* CTA Section */}
          <div className="text-center mt-12">
            <button
              className="font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              style={{
                background: '#0A66C2',
                color: 'white',
                border: 'none',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#004182'}
              onMouseLeave={e => e.currentTarget.style.background = '#0A66C2'}
              onClick={() => openRegistrationPopup()}
            >
              Schedule Mentorship
            </button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* UPSC Courses Section */}
        <section id="upsc" className="mb-20 scroll-mt-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              UPSC Preparation Programs
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive courses designed for every stage of your UPSC journey
            </p>
          </div>
          <div className="space-y-16">
            {upscCourses.map((course, index) => (
              <CourseCard key={course.id} course={course} index={index} />
            ))}
          </div>
        </section>

        {/* MPSC Courses Section */}
        <section id="mpsc" className="mb-20 scroll-mt-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              MPSC Preparation Programs
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Maharashtra-specific preparation with bilingual support
            </p>
          </div>
          <div className="space-y-16">
            {mpscCourses.map((course, index) => (
              <CourseCard key={course.id} course={course} index={index} />
            ))}
          </div>
        </section>

        {/* CAPF Courses Section */}
        <section id="capf" className="mb-20 scroll-mt-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              CAPF Preparation Program
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Central Armed Police Forces preparation with expert guidance
            </p>
          </div>
          <div className="space-y-16">
            {capfCourses.map((course, index) => (
              <CourseCard key={course.id} course={course} index={index} />
            ))}
          </div>
        </section>

        {/* Optional Subjects Section */}
        <section className="mb-10">
          <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-2xl p-6 lg:p-10 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
            <div className="relative">
              <div className="text-center mb-8">
                <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                  Optional Subjects Offered
                </h2>
                <p className="text-base text-gray-300 max-w-2xl mx-auto">
                  Choose the right optional subject with expert guidance and comprehensive preparation
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {optionalSubjects.map((subject, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/20 transition-all duration-300">
                    <h3 className="text-lg font-semibold mb-2">{subject.name}</h3>
                    <p className="text-gray-300 text-sm">{subject.description}</p>
                  </div>
                ))}
              </div>
              <div className="text-center mt-8">
                <p className="text-base text-gray-300">
                  Get personalized guidance to choose the right optional subject based on your background and interests
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Registration Popup */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Course Registration</h3>
                  <p className="text-sm text-gray-600">Join DimensionIAS Today</p>
                </div>
              </div>
              <button
                onClick={closeRegistrationPopup}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleFormSubmit} className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter your full name"
                    required
                  />
                </div>
              </div>

              {/* Contact */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="tel"
                    name="contact"
                    value={formData.contact}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter 10-digit mobile number"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter your email address"
                    required
                  />
                </div>
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter your city"
                  />
                </div>
              </div>

              {/* Course Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Course <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <CourseIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <select
                    name="course"
                    value={formData.course}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none bg-white"
                    required
                  >
                    <option value="">Choose a course</option>
                    <optgroup label="UPSC Courses">
                      {upscCourses.map((course, index) => (
                        <option key={`upsc-${index}`} value={course.title}>
                          {course.title}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="MPSC Courses">
                      {mpscCourses.map((course, index) => (
                        <option key={`mpsc-${index}`} value={course.title}>
                          {course.title}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="CAPF Courses">
                      {capfCourses.map((course, index) => (
                        <option key={`capf-${index}`} value={course.title}>
                          {course.title}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>
              </div>

              {/* Submit Message */}
              {submitMessage && (
                <div className={`p-3 rounded-lg text-sm ${
                  submitMessage.includes('successful') 
                    ? 'bg-green-100 text-green-700 border border-green-200' 
                    : 'bg-red-100 text-red-700 border border-red-200'
                }`}>
                  {submitMessage}
                </div>
              )}

              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeRegistrationPopup}
                  className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-3 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Register Now'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Courses;